import { useMemo } from 'react';
import type { MainContentTab, Tab } from '../../store/layoutStore';
import { extractEndpointIdFromTab } from '../../utils/extractEndpointIdFromTab';
import { useSelectedTarget } from '../../store/projectStore';

type CollaborationEntityType =
  | 'APPLICATION'
  | 'FINDING'
  | 'SIGNAL'
  | 'ENDPOINT';

export type CollaborationContext = {
  entityType: CollaborationEntityType;
  entityId: string | undefined;
  entityName: string;
  tabType: MainContentTab;
};

const SPECIFIC_TAB_TYPE_TO_CONTEXT: Partial<
  Record<MainContentTab, (tab: Tab) => Omit<CollaborationContext, 'tabType'>>
> = {
  finding: (tab) => ({
    entityType: 'FINDING',
    entityId: (tab.data as { findingId?: string })?.findingId,
    entityName: tab.title || 'Finding',
  }),
  signal: (tab) => ({
    entityType: 'SIGNAL',
    entityId: (tab.data as { signalId?: string })?.signalId,
    entityName: tab.title || 'Signal',
  }),
  endpoint: (tab) => ({
    entityType: 'ENDPOINT',
    entityId: extractEndpointIdFromTab({ tab }),
    entityName: tab.title || 'Endpoint',
  }),
};

export function resolveCollaborationContext({
  activeTab,
  applicationId,
  applicationName,
}: {
  activeTab: Tab | undefined;
  applicationId: string | undefined;
  applicationName: string | undefined;
}): CollaborationContext {
  const sharedApplicationContext = {
    entityType: 'APPLICATION' as const,
    entityId: applicationId,
    entityName: applicationName || 'Application',
  };

  if (!activeTab) {
    return {
      ...sharedApplicationContext,
      tabType: 'application',
    };
  }

  const resolver = SPECIFIC_TAB_TYPE_TO_CONTEXT[activeTab.type];
  if (resolver) {
    return {
      ...resolver(activeTab),
      tabType: activeTab.type,
    };
  }

  return {
    ...sharedApplicationContext,
    tabType: 'application',
  };
}

export function useCollaborationContext(
  activeTab: Tab | undefined,
  applicationId: string | undefined,
): CollaborationContext {
  const selectedTarget = useSelectedTarget();
  const applicationName = selectedTarget?.name;

  return useMemo(
    () =>
      resolveCollaborationContext({
        activeTab,
        applicationId,
        applicationName,
      }),
    [activeTab, applicationId, applicationName],
  );
}
